import { SxProps, Theme } from "@mui/material";
import centeredPage from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import { white } from "theme/colors";
import reportsTokens, {
  analyticsToggleTokens,
  PHONE_SM_QUERY,
  reportsChipTabTokens,
  reportsRecordListTokens,
  reportsResponsiveTokens,
} from "./Reports.tokens";

const { header, tabs, focus } = reportsTokens;
const { phone, tablet } = reportsResponsiveTokens;
const chip = reportsChipTabTokens;
const toggle = analyticsToggleTokens;
const list = reportsRecordListTokens;

/**
 * Hides the chip rail's scrollbar the way the reference does (`.chips::
 * -webkit-scrollbar{height:0}`) while the rail itself still scrolls.
 */
const hiddenScrollbar = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": { display: "none", height: 0 },
};

const reportsStyles = {
  /**
   * Inner padding only — the centred measure comes from
   * `CenteredAuthenticatedLayout`, exactly as before.
   *
   * Below `md` the reference lays the page out edge to edge on its own gutters
   * (14/16px on a phone, 26px on the tablet), so that wrapper's gutter and top
   * margin are pulled back off with negative margins first and the reference
   * values applied on top. It also spaces the blocks with one column gap
   * instead of the per-block margins the desktop screen uses. From `md` up
   * nothing changes.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.gap, sm: tablet.gap, md: 0 },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    pt: {
      xs: phone.paddingTop.std,
      sm: tablet.paddingTop,
      md: centeredPage.paddingTop,
    },
    px: {
      xs: phone.paddingX.std,
      sm: tablet.paddingX,
      md: centeredPage.paddingX,
    },
    pb: {
      xs: phone.paddingBottom.std,
      sm: tablet.paddingBottom,
      md: centeredPage.paddingBottom,
    },
    [PHONE_SM_QUERY]: {
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.paddingBottom.sm,
    },
  } as SxProps<Theme>,

  /**
   * Title block and the Insights ▸ Reports switch. The phone stacks them with
   * the switch on top (its `<h1>` has moved to the app bar, so only the
   * strapline is left below); the tablet puts the switch on the right.
   */
  header: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "flex-start" },
    justifyContent: "space-between",
    gap: { xs: phone.gap, sm: tablet.headerGap, md: 0 },
    [PHONE_SM_QUERY]: { gap: phone.gap },
  } as SxProps<Theme>,

  headerText: {
    minWidth: 0,
  } as SxProps<Theme>,

  /** The phone reference carries the screen name in the app bar, not here. */
  title: {
    display: { xs: "none", sm: "block" },
    m: 0,
    color: header.titleColor,
    fontSize: { sm: tablet.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: {
      sm: tablet.titleLetterSpacing,
      md: header.titleLetterSpacing,
    },
    lineHeight: 1.2,
  } as SxProps<Theme>,

  subtitle: {
    m: 0,
    mt: {
      xs: 0,
      sm: tablet.subtitleMarginTop,
      md: header.subtitleMarginTop,
    },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  /**
   * Segmented Insights ▸ Reports switch. `order: -1` lifts it above the
   * strapline on a phone, which is where the reference draws it; the tablet
   * row leaves it where it sits in the DOM.
   */
  viewToggle: {
    display: { xs: "flex", md: "none" },
    order: { xs: -1, sm: 0 },
    alignSelf: { sm: "flex-start" },
    flexShrink: 0,
    // The app does not set a global `border-box`, so the 4px track padding
    // would otherwise be added to the reference's 260px rather than sit inside
    // it.
    boxSizing: "border-box",
    width: { xs: "100%", sm: toggle.tabletWidth },
    gap: toggle.gap,
    p: toggle.padding,
    borderRadius: toggle.radius,
    bgcolor: toggle.bg,
  } as SxProps<Theme>,

  viewTogglePill: (active: boolean) =>
    ({
      flex: 1,
      minWidth: 0,
      height: toggle.pillHeight,
      borderRadius: toggle.pillRadius,
      fontFamily: "inherit",
      fontSize: toggle.pillFontSize,
      fontWeight: toggle.pillFontWeight,
      lineHeight: 1,
      textTransform: "none",
      bgcolor: active ? toggle.activeBg : "transparent",
      color: active ? toggle.activeColor : toggle.idleColor,
      boxShadow: active ? toggle.activeShadow : "none",
      "&:hover": { bgcolor: active ? toggle.activeBg : "transparent" },
      // `ButtonBase` ships `outline: 0`; without this the switch is invisible
      // to a keyboard user (WCAG 2.4.7).
      "&.Mui-focusVisible, &:focus-visible": {
        outline: `${focus.width} solid ${focus.ring}`,
        outlineOffset: "-2px",
      },
    }) as SxProps<Theme>,

  /**
   * Report tabs. The phone turns them into a chip rail that scrolls sideways
   * out to the page gutters; the tablet and desktop keep the underline row, on
   * their own track widths.
   */
  tabBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "nowrap", md: "wrap" },
    gap: { xs: phone.tabsGap.std, sm: tablet.tabsGap, md: tabs.gap },
    overflowX: { xs: "auto", md: "visible" },
    borderBottom: {
      xs: "none",
      sm: `${tabs.borderWidth} solid ${tabs.borderColor}`,
    },
    ...hiddenScrollbar,
    [PHONE_SM_QUERY]: { gap: phone.tabsGap.sm },
  } as SxProps<Theme>,

  /** Wrapper `ScrollHintRail` positions its chevrons against. */
  tabBarRail: {
    mt: { xs: 0, md: tabs.marginTop },
    mb: { xs: 0, md: tabs.marginBottom },
  } as SxProps<Theme>,

  tab: (active: boolean) =>
    ({
      flexShrink: 0,
      minWidth: 0,
      minHeight: 0,
      whiteSpace: "nowrap",
      p: { xs: chip.padding, sm: 0 },
      pb: { sm: tablet.tabPaddingBottom, md: tabs.paddingBottom },
      // The tablet reference pads each label 2px so its 20px gap reads as 24.
      px: { sm: tablet.tabPaddingX, md: 0 },
      mb: { xs: 0, sm: "-1.5px" },
      border: {
        xs: `1px solid ${active ? chip.activeBorder : chip.idleBorder}`,
        sm: "none",
      },
      borderBottom: {
        xs: `1px solid ${active ? chip.activeBorder : chip.idleBorder}`,
        sm: `${tabs.indicatorWidth} solid ${
          active ? tabs.activeColor : "transparent"
        }`,
      },
      borderRadius: { xs: chip.radius, sm: 0 },
      bgcolor: {
        xs: active ? chip.activeBg : chip.idleBg,
        sm: "transparent",
      },
      color: {
        xs: active ? chip.activeColor : chip.idleColor,
        sm: active ? tabs.activeColor : tabs.inactiveColor,
      },
      fontSize: { xs: chip.fontSize, sm: tablet.tabFontSize, md: tabs.fontSize },
      // `Reports.dc.html` sets the selected label to 800 and the rest to 600 at
      // every size that keeps the underline row.
      fontWeight: {
        xs: chip.fontWeight,
        sm: active ? tabs.activeWeight : tabs.inactiveWeight,
      },
      lineHeight: "normal",
      textTransform: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": {
        bgcolor: {
          xs: active ? chip.activeBg : chip.idleBg,
          sm: "transparent",
        },
      },
      // `ButtonBase` ships `outline: 0`, which leaves keyboard users with no
      // indication of where they are (WCAG 2.4.7).
      "&.Mui-focusVisible, &:focus-visible": {
        outline: `${focus.width} solid ${focus.ring}`,
        outlineOffset: focus.offset,
      },
      // The tab keeps its place in the tab order while a report loads — see
      // `ReportsTabsPage`; `aria-disabled` reports the state instead.
      "&[aria-disabled='true']": { cursor: "default" },
    }) as SxProps<Theme>,

  /* ── Phone / tablet record list ─────────────────────────────────────── */

  /** "Select all" on the left, Download on the right. */
  selectionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: list.selectionBar.gap,
  } as SxProps<Theme>,

  selectAllControl: {
    display: "flex",
    alignItems: "center",
    gap: { xs: "8px", sm: "9px" },
    p: 0,
    borderRadius: "6px",
    justifyContent: "flex-start",
    "&:hover": { bgcolor: "transparent" },
    "&.Mui-focusVisible, &:focus-visible": {
      outline: `${focus.width} solid ${focus.ring}`,
      outlineOffset: "3px",
    },
  } as SxProps<Theme>,

  selectAllLabel: {
    fontSize: {
      xs: list.selectionBar.labelFontSize.phone,
      sm: list.selectionBar.labelFontSize.tablet,
    },
    fontWeight: list.selectionBar.labelFontWeight,
    color: list.selectionBar.labelColor,
    lineHeight: 1,
    [PHONE_SM_QUERY]: { fontSize: list.selectionBar.labelFontSize.phoneSm },
  } as SxProps<Theme>,

  /** Decorative box — the row/bar around it carries the checkbox semantics. */
  checkbox: (checked: boolean) =>
    ({
      width: list.checkbox.size,
      height: list.checkbox.size,
      borderRadius: list.checkbox.radius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      border: `${list.checkbox.borderWidth} solid ${
        checked ? list.checkbox.checkedBorder : list.checkbox.borderColor
      }`,
      bgcolor: checked ? list.checkbox.checkedBg : list.checkbox.bg,
      color: list.checkbox.checkColor,
    }) as SxProps<Theme>,

  /** Indeterminate dash the header box shows for a partial selection. */
  checkboxDash: {
    width: "7px",
    height: "2px",
    borderRadius: "2px",
    bgcolor: list.checkbox.checkColor,
  } as SxProps<Theme>,

  /**
   * Phone — a stack of cards. Tablet — one bordered card the rows divide up,
   * so the same nodes serve both.
   */
  recordList: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: list.card.gap, sm: 0 },
    bgcolor: { xs: "transparent", sm: list.list.bg },
    border: { xs: "none", sm: `1px solid ${list.list.border}` },
    borderRadius: { xs: 0, sm: list.list.radius },
    overflow: "hidden",
  } as SxProps<Theme>,

  recordRow: (selected: boolean) =>
    ({
      display: "grid",
      width: "100%",
      textAlign: "left",
      gridTemplateColumns: {
        xs: `${list.checkbox.size}px minmax(0,1fr)`,
        sm: list.list.rowColumns,
      },
      columnGap: { xs: list.row.checkGap, sm: list.list.rowGap },
      rowGap: { xs: list.row.pairMarginTop, sm: 0 },
      alignItems: { xs: "flex-start", sm: "center" },
      p: { xs: list.card.padding, sm: list.list.rowPadding },
      border: {
        xs: `1px solid ${
          selected ? list.card.selectedBorder : list.card.border
        }`,
        sm: "none",
      },
      borderBottom: {
        xs: `1px solid ${
          selected ? list.card.selectedBorder : list.card.border
        }`,
        sm: `1px solid ${list.list.rowDivider}`,
      },
      borderRadius: { xs: list.card.radius, sm: 0 },
      bgcolor: {
        xs: selected ? list.card.selectedBg : list.card.bg,
        sm: selected ? list.card.selectedBg : "transparent",
      },
      "&:last-of-type": { borderBottom: { sm: "none" } },
      "&:hover": {
        bgcolor: {
          xs: selected ? list.card.selectedBg : list.card.bg,
          sm: selected ? list.card.selectedBg : "transparent",
        },
      },
      "&.Mui-focusVisible, &:focus-visible": {
        outline: `${focus.width} solid ${focus.ring}`,
        outlineOffset: "-2px",
      },
    }) as SxProps<Theme>,

  recordCheck: {
    gridColumn: 1,
    gridRow: 1,
    mt: { xs: "2px", sm: 0 },
  } as SxProps<Theme>,

  recordText: {
    gridColumn: 2,
    gridRow: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  recordName: {
    display: "block",
    fontSize: {
      xs: list.row.nameFontSize.phone,
      sm: list.row.nameFontSize.tablet,
    },
    fontWeight: list.row.nameFontWeight,
    color: list.row.nameColor,
    lineHeight: 1.3,
    whiteSpace: { sm: "nowrap" },
    overflow: { sm: "hidden" },
    textOverflow: { sm: "ellipsis" },
    [PHONE_SM_QUERY]: { fontSize: list.row.nameFontSize.phoneSm },
  } as SxProps<Theme>,

  recordSub: {
    display: "block",
    mt: list.row.subMarginTop,
    fontSize: {
      xs: list.row.subFontSize.phone,
      sm: list.row.subFontSize.tablet,
    },
    color: list.row.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: list.row.subFontSize.phoneSm },
  } as SxProps<Theme>,

  /** "Sent: 4", "Completed: 0" … — the columns the table can no longer show. */
  recordPairs: {
    gridColumn: { xs: 2, sm: 3 },
    gridRow: { xs: 2, sm: 1 },
    display: "flex",
    flexWrap: "wrap",
    gap: list.row.pairGap,
    justifyContent: { xs: "flex-start", sm: "flex-end" },
    minWidth: 0,
  } as SxProps<Theme>,

  recordPair: {
    p: { xs: list.pair.padding.phone, sm: list.pair.padding.tablet },
    borderRadius: list.pair.radius,
    bgcolor: list.pair.bg,
    color: list.pair.color,
    fontSize: { xs: list.pair.fontSize.phone, sm: list.pair.fontSize.tablet },
    fontWeight: list.pair.fontWeight,
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: {
      padding: list.pair.padding.phoneSm,
      fontSize: list.pair.fontSize.phoneSm,
    },
  } as SxProps<Theme>,

  recordEmpty: {
    bgcolor: list.empty.bg,
    border: `1px solid ${list.empty.border}`,
    borderRadius: list.empty.radius,
    p: { xs: list.empty.padding.phone, sm: list.empty.padding.tablet },
    textAlign: "center",
  } as SxProps<Theme>,

  recordEmptyTitle: {
    fontSize: {
      xs: list.empty.titleFontSize.phone,
      sm: list.empty.titleFontSize.tablet,
    },
    fontWeight: 700,
    color: list.empty.titleColor,
  } as SxProps<Theme>,

  recordEmptySub: {
    mt: list.empty.subMarginTop,
    fontSize: {
      xs: list.empty.subFontSize.phone,
      sm: list.empty.subFontSize.tablet,
    },
    color: list.empty.subColor,
    lineHeight: 1.5,
  } as SxProps<Theme>,

  recordLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: "48px",
  } as SxProps<Theme>,

  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: list.pager.gap,
    mt: list.pager.marginTop,
  } as SxProps<Theme>,

  pagerLabel: {
    fontSize: list.pager.fontSize,
    color: list.pager.color,
    fontWeight: 600,
  } as SxProps<Theme>,

  pagerButton: {
    width: list.pager.buttonSize,
    height: list.pager.buttonSize,
    borderRadius: list.pager.buttonRadius,
    border: `1px solid ${list.pager.buttonBorder}`,
    bgcolor: white,
    color: list.pager.buttonColor,
    "&.Mui-disabled": { opacity: 0.45 },
    "&.Mui-focusVisible, &:focus-visible": {
      outline: `${focus.width} solid ${focus.ring}`,
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,
};

export default reportsStyles;
